import {
  add,
  addDays,
  differenceInCalendarDays,
  differenceInDays,
  endOfDay,
  format,
  parseISO,
  set,
  startOfDay,
  subDays,
  subMonths
} from 'date-fns';
import { formatInTimeZone, format as formatTz, utcToZonedTime } from 'date-fns-tz';
import moment from 'moment-timezone';

class DateUtils {
  static getTomorrow(dateFormat?: string): Date | string {
    const tomorrow = add(new Date(), { days: 1 });
    if (dateFormat) {
      return this.getDateInFormat({
        date: tomorrow,
        dateFormat
      });
    }
    return tomorrow;
  }

  static getNow(): Date {
    return new Date();
  }

  static getCurrentUTCTimestamp(): string {
    return new Date().toISOString();
  }

  static getToday(dateFormat?: string): Date | string {
    const today = new Date();
    if (dateFormat) {
      return format(today, dateFormat);
    }
    return today;
  }

  static getStartOfYear(year: number): Date {
    const startOfYear = new Date(year + '-01-01');
    return startOfYear;
  }

  static getEndOfYear(year: number): Date {
    const endOfYear = new Date(year + '-12-31');
    return endOfYear;
  }

  static getYesterday(dateFormat?: string): Date | string {
    const yesterday = subDays(new Date(), 1);
    if (dateFormat) {
      return this.getDateInFormat({
        date: yesterday,
        dateFormat
      });
    }
    return yesterday;
  }

  static parseISODateString(dateString: string): Date {
    return parseISO(dateString);
  }

  static getStartOfDay(date: Date | string): Date {
    const dateObject = typeof date === 'string' ? parseISO(date) : date;
    return startOfDay(dateObject);
  }

  static getEndOfDay(date: Date | string): Date {
    const dateObject = typeof date === 'string' ? parseISO(date) : date;
    return endOfDay(dateObject);
  }

  static addDaysToDate(date: Date | string, numberOfDays: number): Date {
    const dateObject = typeof date === 'string' ? parseISO(date) : date;
    return addDays(dateObject, numberOfDays);
  }

  static subtractDaysFromDate(date: Date | string, numberOfDays: number): Date {
    const dateObject = typeof date === 'string' ? parseISO(date) : date;
    return subDays(dateObject, numberOfDays);
  }

  static calculateDaysDifference(laterDate: Date | string | null, earlierDate: Date | string | null): number {
    if (!laterDate || !earlierDate) {
      return null;
    }

    const laterDateObject = typeof laterDate === 'string' ? parseISO(laterDate) : laterDate;
    const earlierDateObject = typeof earlierDate === 'string' ? parseISO(earlierDate) : earlierDate;
    return differenceInDays(laterDateObject, earlierDateObject);
  }

  static subtractMonthsFromDate(date: Date | string, numberOfMonths: number): Date {
    const dateObject = typeof date === 'string' ? parseISO(date) : date;
    return subMonths(dateObject, numberOfMonths);
  }

  static getTimezoneLongName() {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZone;
  }

  static getDateInFormat({ date, dateFormat = 'yyyy-MM-dd' }: { date: Date; dateFormat: string }): string {
    if (!date) return '';
    return format(date, dateFormat);
  }

  static updateDatePreservingTime({ date, newDate }: { date: Date; newDate: Date }): Date {
    const updatedDate = new Date(date);

    // Update only the date part
    updatedDate.setDate(newDate.getDate());
    updatedDate.setMonth(newDate.getMonth());
    updatedDate.setFullYear(newDate.getFullYear());

    return updatedDate;
  }

  static getDateInFormatWtTimeZone({
    date,
    dateFormat = 'yyyy-MM-dd',
    timeZone
  }: {
    date: Date;
    dateFormat: string;
    timeZone: string;
  }) {
    return formatInTimeZone(date, timeZone, dateFormat);
  }
  static getMonthInStringFormat(monthNumber: number): string {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    return monthNames[monthNumber - 1];
  }

  static getDifferenceInDays({
    primaryDate = new Date(),
    secondaryDate
  }: {
    primaryDate?: Date;
    secondaryDate: Date;
  }): number {
    const days = differenceInCalendarDays(primaryDate, secondaryDate);
    return days;
  }

  static fromNowRelativeString(
    date,
    nowDate = Date.now(),
    rft = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  ) {
    const SECOND = 1000;
    const MINUTE = 60 * SECOND;
    const HOUR = 60 * MINUTE;
    const DAY = 24 * HOUR;
    const WEEK = 7 * DAY;
    const MONTH = 30 * DAY;
    const YEAR = 365 * DAY;
    const intervals = [
      { ge: YEAR, divisor: YEAR, unit: 'year' },
      { ge: MONTH, divisor: MONTH, unit: 'month' },
      { ge: WEEK, divisor: WEEK, unit: 'week' },
      { ge: DAY, divisor: DAY, unit: 'day' },
      { ge: HOUR, divisor: HOUR, unit: 'hour' },
      { ge: MINUTE, divisor: MINUTE, unit: 'minute' },
      { ge: 30 * SECOND, divisor: SECOND, unit: 'seconds' },
      { ge: 0, divisor: 1, text: 'just now' }
    ];
    // @ts-ignore
    const now = typeof nowDate === 'object' ? nowDate?.getTime() : new Date(nowDate).getTime();
    const diff = now - (typeof date === 'object' ? date : new Date(date)).getTime();
    const diffAbs = Math.abs(diff);
    for (const interval of intervals) {
      if (diffAbs >= interval.ge) {
        const x = Math.round(Math.abs(diff) / interval.divisor);
        const isFuture = diff < 0;
        // @ts-ignore
        return !interval.unit ? interval.text : rft.format(isFuture ? x : -x, interval.unit);
      }
    }
  }

  static startDateToUTCString(date: string): string {
    const originalDate = new Date(date + 'T00:00:00');
    if (date) {
      return originalDate.toISOString();
    }
    return null;
  }

  static getDayOfWeek(date: string): string {
    const originalDate = new Date(date);
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return daysOfWeek[originalDate.getDay()];
  }

  static endDateToUTCString(date: string): string {
    const originalDate = new Date(date + 'T23:59:59');
    if (date) {
      return originalDate.toISOString();
    }
    return null;
  }

  static getUserTimeZoneAbbreviation(): string {
    try {
      const date = new Date();

      // Get timezone abbreviation using Intl.DateTimeFormat with short timeZoneName
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short'
      });

      const parts = formatter?.formatToParts(date);
      const timeZonePart = parts?.find(part => part.type === 'timeZoneName');

      return timeZonePart?.value || '';
    } catch (e) {
      return '';
    }
  }

  static getUserTimeZoneInfo(): { abbreviation: string; fullName: string; identifier: string } {
    try {
      const date = new Date();

      // Get timezone identifier (e.g., "America/New_York")
      const identifier = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Get timezone abbreviation (e.g., "EST")
      const shortFormatter = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short'
      });
      const shortParts = shortFormatter.formatToParts(date);
      const abbreviation = shortParts.find(part => part.type === 'timeZoneName')?.value || '';

      // Get timezone full name (e.g., "Eastern Standard Time")
      const longFormatter = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'long'
      });
      const longParts = longFormatter.formatToParts(date);
      const fullName = longParts.find(part => part.type === 'timeZoneName')?.value || '';

      return {
        abbreviation,
        fullName,
        identifier
      };
    } catch (e) {
      return {
        abbreviation: '',
        fullName: '',
        identifier: ''
      };
    }
  }

  static getTimeZoneAbbreviationForDate(date: Date, timeZone?: string): string {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeZoneName: 'short'
      });

      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');

      return timeZonePart?.value || '';
    } catch (e) {
      return '';
    }
  }

  static getDateInFormatwithTimezone({ date, dateFormat = 'yyyy-MM-dd' }: { date: Date; dateFormat: string }): string {
    const { identifier, fullName } = this.getUserTimeZoneInfo();
    const timezone = identifier ? moment.tz(identifier)?.format('z') || '' : fullName || '';

    return `${format(date, dateFormat)} ${timezone}`;
  }

  static formatTime(dateString: string): string {
    // Create a Date object from the given date string
    const date = new Date(dateString);

    // Extract hours and minutes
    let hours = date.getHours();
    const minutes = date.getMinutes();

    // Determine AM or PM
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // Adjust hours to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const hoursString = hours < 10 ? '0' + hours : hours;

    // Format minutes with leading zero if necessary
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;

    // Construct the formatted time string
    const formattedTime = hoursString + ':' + minutesStr + ' ' + ampm;

    return formattedTime;
  }

  static getUTCStartDate(date: Date): string {
    const utcDate = new Date(date);
    utcDate.setHours(0, 0, 0, 0);
    return utcDate.toISOString();
  }

  static getUTCEndDate(date: Date): string {
    const utcDate = new Date(date);
    utcDate.setHours(23, 59, 59, 999);
    return utcDate.toISOString();
  }

  static ensureUTCStartDateFormat(dateString: string): string {
    if (!dateString) return '';

    // If already in ISO format with time component (contains 'T')
    if (dateString.includes('T')) {
      return dateString;
    }

    // If it's a date string without time, convert to start of day in UTC
    return this.startDateToUTCString(dateString);
  }

  static ensureUTCEndDateFormat(dateString: string): string {
    if (!dateString) return '';

    // If already in ISO format with time component
    if (dateString.includes('T')) {
      return dateString;
    }

    // If it's a date string without time, convert to end of day in UTC
    return this.endDateToUTCString(dateString);
  }

  static formatTimeWithTimeZone({
    date,
    timeZone,
    dateFormat = 'MMM dd, yyyy'
  }: {
    date: Date | string;
    timeZone: string;
    dateFormat?: string;
  }): string {
    if (!date || !timeZone) return '';

    // Convert input to Date if string
    const inputDate = typeof date === 'string' ? new Date(date) : date;

    // Convert UTC to zoned time
    const zonedDate = utcToZonedTime(inputDate, timeZone);

    // Format with timezone
    return formatTz(zonedDate, dateFormat, { timeZone });
  }

  static formatDateTimeToMidnight(date: Date): string {
    const midnight = set(date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

    // eslint-disable-next-line quotes
    const formatted = format(midnight, "yyyy-MM-dd'T'HH:mm:ss'Z'");
    return formatted;
  }
  static getDaysDifference(date: Date): string {
    const days = this.getDifferenceInDays({ primaryDate: date, secondaryDate: new Date() });

    if (days === -1) return 'Yesterday';
    if (days < 0) return `${Math.abs(days)} days ago`;
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `In ${days} days`;
  }

  static areSameDates(date1: Date, date2: Date): boolean {
    if (!date1 || !date2) return false;
    return (
      this.getDateInFormat({ date: date1, dateFormat: 'yyyy-MM-dd' }) ===
      this.getDateInFormat({ date: date2, dateFormat: 'yyyy-MM-dd' })
    );
  }
}

export default DateUtils;
